import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import { useDispatch } from 'react-redux';
import { createImmutableStateInvariantMiddleware } from '@reduxjs/toolkit';

import { FETCH_STATUSES, isBrowser } from '@tager/web-core';

import {
  convertAliasToPathQuery,
  convertSlugToPath,
  getQueryParams,
} from '@/utils/common';
import { useTypedSelector } from '@/store/store';
import {
  Category,
  currentPageAdded,
  getCategoryProductsByIdThunk,
  getCategoryRelationsByIdThunk,
  getcategoryRelationsExtendedByIdThunk,
  selectCategoryByAlias,
  selectCategoryProductsByAlias,
  selectCategoryRelationsByAlias,
  selectCategoryRelationsExtendedByAlias,
  selectCurrentPage,
} from '@/store/reducers/pages/catalog/catalog';
import { fetchProductFiltersByProductIdThunk } from '@/store/reducers/pages/filters/CatalogProductFilters/filters';
import { DEFAULT_SEARCH_PARAMS } from '@/constants/search';
import { QUERY_DEFAULT_VALUES } from '@/constants/catalog';
import { CATEGORY_TYPE } from '@/modules/Catalog/types';
import CatalogView from '@/modules/Catalog/CatalogView';
import { usePrevTotal } from '@/modules/Catalog/helpers';

function Catalog() {
  const dispatch = useDispatch();
  const router = useRouter();
  const activePage = useTypedSelector(selectCurrentPage);
  const alias = convertAliasToPathQuery(convertSlugToPath(router.query.alias));
  const currentCategory: Category = useTypedSelector((state) =>
    selectCategoryByAlias(state, alias)
  );

  const relations = useTypedSelector((state) =>
    selectCategoryRelationsByAlias(state, alias)
  );
  const relationsExtended = useTypedSelector((state) =>
    selectCategoryRelationsExtendedByAlias(state, alias)
  );
  const products = useTypedSelector((state) =>
    selectCategoryProductsByAlias(state, alias)
  );
  const prevPagesAmount = usePrevTotal(products?.meta?.total);

  const pageAmount =
    (products?.status === FETCH_STATUSES.LOADING
      ? prevPagesAmount
      : products?.meta?.total) ?? 0;

  const isEmptyCatalog =
    (products?.status === FETCH_STATUSES.SUCCESS &&
      products?.meta?.total &&
      products.meta.total > 0 &&
      products.data.length === 0) ||
    products?.meta?.total === 0;

  const onPageChange = (page: number) => {
    const params = {
      [DEFAULT_SEARCH_PARAMS.PAGE_LIMIT]: QUERY_DEFAULT_VALUES.PageLimit,
      [DEFAULT_SEARCH_PARAMS.PAGE_OFF_SET]:
        QUERY_DEFAULT_VALUES.PageLimit * (page - 1),
      [DEFAULT_SEARCH_PARAMS.PAGE]: page,
    };

    if (currentCategory.data?.id) {
      dispatch(
        getCategoryProductsByIdThunk(alias, currentCategory.data.id, {
          ...getQueryParams(),
          ...params,
        })
      );
      router.replace(
        {
          query: { ...router.query, [DEFAULT_SEARCH_PARAMS.PAGE]: page },
        },
        undefined,
        { scroll: true, shallow: true }
      );
      dispatch(currentPageAdded(page));
    }
  };

  useEffect(() => {
    if (!currentCategory.data?.id || !isBrowser()) {
      return;
    }
    switch (currentCategory.data.type) {
      case CATEGORY_TYPE.catalog:
        dispatch(getCategoryRelationsByIdThunk(alias, currentCategory.data.id));
        dispatch(
          getcategoryRelationsExtendedByIdThunk(alias, currentCategory.data.id)
        );
        break;
      case CATEGORY_TYPE.product:
        const params = getQueryParams() ?? {};
        const pageFromUrl = params[DEFAULT_SEARCH_PARAMS.PAGE];
        if (pageFromUrl) {
          params[DEFAULT_SEARCH_PARAMS.PAGE_OFF_SET] = String(
            QUERY_DEFAULT_VALUES.PageLimit * (Number(pageFromUrl) - 1)
          );
          dispatch(currentPageAdded(Number(pageFromUrl)));
        }
        dispatch(currentPageAdded(Number(1)));
        dispatch(
          getCategoryProductsByIdThunk(alias, currentCategory.data.id, params)
        );
        dispatch(fetchProductFiltersByProductIdThunk(currentCategory.data.id));
        break;
      default:
        break;
    }
    return () => {
      dispatch(currentPageAdded(1));
    };
  }, [alias, currentCategory.data?.id, currentCategory.data?.type, dispatch]);

  return (
    <Component>
      <CatalogView
        alias={alias}
        category={currentCategory}
        relations={relations}
        relationsExtended={relationsExtended}
        products={products}
        pageAmount={pageAmount}
        activePage={activePage}
        isEmptyCatalog={isEmptyCatalog}
        onChange={onPageChange}
      />
    </Component>
  );
}

const Component = styled.div``;

export default Catalog;
